import { Link } from '@tanstack/react-router';
import {
  ArrowRight,
  FileText,
  FileVideo,
  HelpCircle,
  Lock,
  Mail,
  Radio,
  Zap,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Badge, StatusDot, buttonClasses, Card, SectionHeader, EmptyState, ErrorState, LoadingState } from '@pixlide/ui';
import { formatRelative } from '@/lib/format';
import { SUPPORT_MAILTO } from '@/lib/support';
import { useAuthStore } from '@/stores/auth-store';
import { useBroadcasts, useProjects, useScreens } from '@/api/queries/dashboard';

/**
 * Statut d'une commande de création sur-mesure, tel qu'affiché au client.
 *
 * On regroupe Brouillon/Rendu en cours sous un seul « En cours » : la distinction entre
 * les deux n'intéresse que l'équipe Pixlide qui traite la commande, pas le client qui
 * attend juste de savoir si sa vidéo est prête.
 */
const CUSTOM_ORDER_STATUS: Record<number, { label: string; tone: 'brand' | 'success' | 'danger' }> = {
  0: { label: 'En cours', tone: 'brand' }, // Draft
  1: { label: 'En cours', tone: 'brand' }, // Rendering
  2: { label: 'Terminé', tone: 'success' }, // Ready
  3: { label: 'Échec', tone: 'danger' }, // Failed
};

const DEFAULT_CUSTOM_ORDER_STATUS = { label: 'En cours', tone: 'brand' } as const;

/** Cartes empilées avec lecture, tracé exact de la maquette Figma. */
function TemplateIcon() {
  return (
    <svg aria-hidden className="size-12" viewBox="0 0 101 90" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M93.0392 8.73371L39.2792 1.14371C36.4392 0.743714 33.9592 1.11371 31.8692 3.04371C30.1492 4.63371 29.2992 7.05371 29.2092 9.79371L19.4092 10.1037C14.9592 10.2437 11.4692 13.4337 11.3592 17.9737C9.91919 18.4437 8.49919 18.5137 7.07919 18.8437C2.75919 19.8337 0.379189 23.7537 1.13919 28.1337L10.5592 82.0437C11.2792 86.1837 15.1092 88.9537 19.2692 88.2237L71.7892 79.1037C74.0492 78.7137 75.9092 79.4137 77.8092 78.6337C79.0092 78.1437 80.0992 77.9237 81.5192 78.3037C85.9492 79.5037 90.8292 76.9437 91.5292 72.0537L99.3192 17.2637C99.8892 13.2837 97.0692 9.29373 93.0292 8.72373L93.0392 8.73371ZM18.7992 86.0337C15.7992 86.5537 13.3392 84.3337 12.7392 81.5937L3.3092 27.3537C2.8292 24.5837 4.41921 22.0537 6.99921 21.2137C8.49921 20.7237 10.0192 20.5637 11.4992 20.3937L13.1792 73.3837C13.3192 77.6637 16.9692 80.6537 21.1092 80.5937L54.6492 79.8037L18.7992 86.0337ZM21.1592 78.4137C17.9392 78.5137 15.4192 76.0337 15.3292 72.8437L13.6592 17.6337C13.5692 14.7437 16.2592 12.5337 18.8392 12.3837C22.2692 12.1937 25.4592 12.0737 28.8292 12.1237L21.8192 62.6937C21.3192 66.2737 24.2192 70.0637 27.8192 70.5837L71.0292 76.8837L21.1692 78.4137H21.1592ZM89.4392 71.6937C89.0392 74.5737 86.6592 76.3537 83.6492 76.3837L28.4892 68.6537C25.2892 67.8737 23.6492 65.2337 24.1092 61.9737C24.1092 61.9737 31.5492 7.81374 31.9592 6.82374C33.2192 3.77374 35.7592 2.90372 38.8092 3.33372L92.2392 10.8737C95.2092 11.2937 97.2292 13.6437 97.1292 16.5737C97.0892 17.8637 89.4292 71.6837 89.4292 71.6837L89.4392 71.6937Z"
        fill="#141414"
        stroke="#141414"
        strokeWidth="2"
      />
      <path
        d="M74.4791 42.4436L48.9291 53.8436C48.2391 54.1536 47.4691 53.8236 46.9991 53.4636C46.6391 53.1836 46.1891 52.5136 46.2691 51.7436L49.2791 24.1536C49.3791 23.2636 49.8391 22.6036 50.3991 22.3236C51.2091 21.9036 51.9791 22.1036 52.7491 22.6736L74.7791 39.0136C75.3791 39.4536 75.6191 40.0336 75.6191 40.5736C75.6191 41.1836 75.4091 42.0336 74.4891 42.4436H74.4791ZM69.5791 40.1736L52.9191 27.8136L50.6291 48.6936L69.5791 40.1836V40.1736Z"
        fill="#141414"
      />
    </svg>
  );
}

/** Crayon en diagonale avec étincelles, tracé exact de la maquette Figma. */
function CustomOrderIcon() {
  return (
    <svg aria-hidden className="size-12" viewBox="0 0 71 71" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10.65 1.92C13.21 -0.64 17.35 -0.64 19.91 1.92L27.76 9.77001C28.77 10.78 28.77 12.41 27.76 13.42C26.75 14.43 25.12 14.43 24.11 13.42L22.08 11.39L18.22 15.25C17.21 16.26 15.58 16.26 14.57 15.25C13.56 14.24 13.56 12.61 14.57 11.6L18.43 7.74001L16.25 5.55998C15.71 5.01998 14.83 5.01998 14.29 5.55998L5.57002 14.28C5.03002 14.82 5.03002 15.7 5.57002 16.24L13.42 24.09C14.43 25.1 14.43 26.73 13.42 27.74C12.41 28.75 10.78 28.75 9.76998 27.74L1.92 19.89C-0.64 17.33 -0.64 13.19 1.92 10.63L10.64 1.90999L10.65 1.92Z"
        fill="#141414"
      />
      <path
        d="M59.6799 41.7001C58.6699 40.6901 57.0399 40.6901 56.0299 41.7001C55.0199 42.7101 55.0199 44.3401 56.0299 45.3501L59.1199 48.4401L54.8999 52.6601C53.8899 53.6701 53.8899 55.3001 54.8999 56.3101C55.9099 57.3201 57.5399 57.3201 58.5499 56.3101L62.7699 52.0901L65.3099 54.6301C65.8499 55.1701 65.8499 56.0501 65.3099 56.5901L56.5899 65.3101C56.0499 65.8501 55.1699 65.8501 54.6299 65.3101L45.3499 56.0301C44.3399 55.0201 42.7099 55.0201 41.6999 56.0301C40.6899 57.0401 40.6899 58.6701 41.6999 59.6801L50.9799 68.9601C53.5399 71.5201 57.6799 71.5201 60.2399 68.9601L68.9599 60.2301C71.5199 57.6701 71.5199 53.5301 68.9599 50.9701L59.6799 41.6901V41.7001Z"
        fill="#141414"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M65.0596 4.68001C59.2496 -1.12999 49.8396 -1.12999 44.0396 4.68001L6.40958 42.38C5.49958 43.29 4.8796 44.46 4.6196 45.72L0.529576 66.18C0.359576 67.02 0.629588 67.9 1.22959 68.51C1.83959 69.12 2.7096 69.38 3.5596 69.22L24.0896 65.13C25.3596 64.88 26.5196 64.25 27.4396 63.34L65.0596 25.72C70.8696 19.91 70.8696 10.5 65.0596 4.7V4.68001ZM15.9096 40.15L42.0796 13.93L55.8096 27.66L29.5696 53.89C29.2596 53.46 28.9096 53.05 28.5196 52.66C26.4296 50.57 23.7096 49.73 21.7396 49.36C21.3496 49.29 20.9796 49.23 20.6396 49.19C20.5996 48.82 20.5396 48.41 20.4596 47.99C20.0996 46.02 19.2496 43.3 17.1596 41.21C16.7596 40.81 16.3396 40.46 15.9096 40.15ZM59.4496 24.02L45.7196 10.29L47.6796 8.31999C51.4696 4.52999 57.6196 4.52999 61.4096 8.31999C65.1996 12.11 65.1996 18.26 61.4096 22.05L59.4496 24.01V24.02ZM12.1696 43.89L10.0496 46.01C9.8596 46.2 9.71959 46.45 9.66959 46.72L6.33957 63.39L23.0796 60.05C23.3496 60 23.5996 59.86 23.7896 59.67L25.8296 57.63C25.5596 57.13 25.2396 56.68 24.8696 56.31C23.8496 55.29 22.3196 54.72 20.7996 54.44C20.0696 54.31 19.4196 54.25 18.9596 54.23C18.7296 54.23 18.5496 54.22 18.4296 54.22C18.3696 54.22 18.3296 54.22 18.3096 54.22H18.2896C16.8696 54.29 15.6696 53.19 15.5996 51.77C15.5996 51.71 15.5996 51.65 15.5996 51.6C15.5996 51.55 15.5996 51.49 15.5996 51.44V51.42C15.5996 51.42 15.5996 51.36 15.5996 51.3C15.5996 51.18 15.5996 51 15.5896 50.77C15.5696 50.31 15.5196 49.66 15.3796 48.93C15.0996 47.41 14.5296 45.88 13.5096 44.86C13.1296 44.48 12.6796 44.16 12.1696 43.89Z"
        fill="#141414"
      />
    </svg>
  );
}

/** Horloge avec flèche de rappel, tracé exact de la maquette Figma. */
function HistoryIcon() {
  return (
    <svg aria-hidden className="size-12" viewBox="0 0 92 90" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M48.4014 16.3991C48.7814 17.0891 50.0314 17.4992 50.0314 18.6092L50.0714 50.0891C50.0714 51.5191 48.9414 52.6191 47.5314 52.6191H24.2114C22.6214 52.6191 21.6914 51.1791 21.7814 49.8991C21.8814 48.5091 23.0415 47.5692 24.6215 47.5692H44.9714L45.0714 18.2292C45.3114 17.3592 46.3714 16.9892 46.6714 16.3892H48.4014V16.3991Z"
        fill="#141414"
      />
      <path
        d="M73.2213 57.3992L83.4413 53.4792C84.9813 52.8892 86.7413 53.7892 87.3013 55.2892L91.0613 65.3992C91.6713 67.0492 90.7413 68.6592 89.2213 69.3092C88.0213 69.8192 86.3812 69.4392 85.4012 68.0592C84.8912 67.3292 84.9713 66.3292 84.0713 65.6492C73.6513 84.7492 51.6113 93.7792 30.8813 87.0992C10.8613 80.6492 -2.13874 60.7792 0.29126 39.4092C2.63126 18.8292 19.5113 2.16921 40.3613 0.19921C61.5513 -1.80079 81.4213 11.4292 87.4613 32.2092C87.9713 33.9692 86.5413 35.4992 85.1713 35.8592C83.5213 36.2892 81.8413 35.4792 81.3013 33.6992C75.9213 16.0192 58.9613 4.77919 40.7812 6.58919C23.0012 8.35919 8.63135 22.5692 6.63135 40.1692C4.63135 57.7692 15.6013 75.3792 32.6613 80.9692C50.4313 86.7992 69.6313 79.0292 78.4513 62.5092C76.7913 62.4392 73.0913 65.2792 71.4513 61.8992C70.5913 60.1292 71.3113 58.1292 73.2213 57.3992Z"
        fill="#141414"
      />
    </svg>
  );
}

function ActionCard({
  to,
  icon,
  title,
  description,
  badge,
  locked = false,
}: {
  to: string;
  icon: ReactNode;
  title: string;
  description: string;
  badge?: ReactNode;
  locked?: boolean;
}) {
  return (
    <Card raised className="relative flex flex-col items-center gap-4 p-7 text-center transition hover:-translate-y-0.5">
      {badge ? <div className="absolute top-4 right-4">{badge}</div> : null}

      <span aria-hidden className="text-ink-900">
        {icon}
      </span>

      <h3 className="text-lg leading-snug font-semibold">{title}</h3>
      <p className="text-sm text-ink-500">{description}</p>

      {locked ? (
        <span
          aria-hidden
          className="flex size-7 items-center justify-center rounded-full bg-ink-100 text-ink-500"
        >
          <Lock className="size-3.5" />
        </span>
      ) : null}

      {/* Le lien couvre toute la carte : la zone cliquable correspond à la zone
          visuellement cliquable, et le libellé accessible reste le titre de la carte. */}
      <Link to={to} className="absolute inset-0 rounded-card" aria-label={title}>
        <span className="sr-only">{title}</span>
      </Link>
    </Card>
  );
}

/**
 * Tableau de bord : premier écran après connexion.
 *
 * Reprend la structure de la maquette du board produit — salutation, accès support, trois
 * cartes d'action, section « Mises à jour » — et y ajoute l'état réel du parc d'écrans.
 * Ce dernier point est un écart volontaire avec la maquette : un écran noir en salle est
 * invisible pour nous et très visible pour le client, il doit donc apparaître dès l'accueil
 * et pas seulement dans un onglet dédié.
 */
export function DashboardPage() {
  const user = useAuthStore((state) => state.user);
  const screens = useScreens();
  const drafts = useProjects({ status: 0, pageSize: 3 });
  // status 1 = BroadcastStatus.Scheduled, la seule qui signifie « à l'antenne maintenant ».
  const activeBroadcasts = useBroadcasts({ status: 1, pageSize: 1 });
  // origin 1 = VideoProjectOrigin.CustomOrder ; le backend trie déjà par dernière modification.
  const latestCustomOrder = useProjects({ origin: 1, pageSize: 1 }).data?.items[0];
  const customOrderBadge = latestCustomOrder
    ? (CUSTOM_ORDER_STATUS[latestCustomOrder.status] ?? DEFAULT_CUSTOM_ORDER_STATUS)
    : null;

  const offlineScreens = (screens.data?.items ?? []).filter(
    (screen) => screen.isPaired && !screen.isOnline,
  );

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-10">
      <section>
        <h1 className="text-3xl tracking-tight">
          Bonjour <span className="font-semibold">{user?.firstName ?? ''},</span>
        </h1>
        <p className="mt-1.5 text-ink-500">Que souhaitez-vous faire aujourd'hui ?</p>

        {activeBroadcasts.data ? (
          <p className="mt-2 flex items-center gap-1.5 text-sm text-ink-500">
            <Radio aria-hidden className="size-4" />
            <span className="font-medium text-ink-700">{activeBroadcasts.data.totalCount}</span>
            vidéo{activeBroadcasts.data.totalCount > 1 ? 's' : ''} actuellement diffusée
            {activeBroadcasts.data.totalCount > 1 ? 's' : ''} sur vos écrans
          </p>
        ) : null}

        <div className="mt-5 flex flex-wrap gap-3">
          <Link to="/messages" className={buttonClasses({ size: 'sm' })}>
            <HelpCircle aria-hidden className="size-4" />
            Chat Support
          </Link>

          <a href={SUPPORT_MAILTO} className={buttonClasses({ size: 'sm', variant: 'secondary' })}>
            <Mail aria-hidden className="size-4" />
            Email Support
          </a>
        </div>
      </section>

      <section aria-labelledby="actions-heading">
        <h2 id="actions-heading" className="sr-only">
          Actions principales
        </h2>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <ActionCard
            to="/creer"
            icon={<TemplateIcon />}
            title="Créer à partir d'un template"
            description="Partez d'une vidéo existante et adaptez-la avec vos éléments."
          />

          <ActionCard
            to="/creer/sur-mesure"
            icon={<CustomOrderIcon />}
            title="Créer une vidéo sur-mesure"
            description="Envoyez-nous vos éléments et on s'occupe du reste."
            badge={
              customOrderBadge ? (
                <Badge tone={customOrderBadge.tone}>{customOrderBadge.label}</Badge>
              ) : (
                <Badge tone="neutral" icon={<Zap className="size-3.5" />}>
                  Version pro
                </Badge>
              )
            }
            locked
          />

          <ActionCard
            to="/videos"
            icon={<HistoryIcon />}
            title="Anciennes créations"
            description="Accédez à toutes vos vidéos et vos brouillons exportées."
          />
        </div>
      </section>

      {/* Alerte d'exploitation : n'apparaît que s'il y a réellement un problème. Une bannière
          permanente finit par être ignorée, ce qui la rend inutile le jour où elle compte. */}
      {offlineScreens.length > 0 ? (
        <Card className="flex flex-wrap items-center gap-4 border-l-4 border-warning-500 p-5">
          <div className="flex-1">
            <p className="font-medium">
              {offlineScreens.length} écran{offlineScreens.length > 1 ? 's' : ''} hors ligne
            </p>
            <p className="mt-0.5 text-sm text-ink-500">
              {offlineScreens
                .slice(0, 3)
                .map(
                  (screen) =>
                    `${screen.name}${screen.lastHeartbeatAt ? ` (vu ${formatRelative(screen.lastHeartbeatAt)})` : ''}`,
                )
                .join(', ')}
            </p>
          </div>

          <Link to="/ecrans" className={buttonClasses({ variant: 'secondary', size: 'sm' })}>
            Voir les écrans
          </Link>
        </Card>
      ) : null}

      <section aria-labelledby="updates-heading">
        <SectionHeader
          title="Mises à jour"
          action={
            <Link
              to="/videos"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-700 hover:text-ink-900"
            >
              Voir tout
              <ArrowRight aria-hidden className="size-4" />
            </Link>
          }
        />
        <h2 id="updates-heading" className="sr-only">
          Mises à jour
        </h2>

        {drafts.isPending ? <LoadingState /> : null}

        {drafts.isError ? (
          <ErrorState
            message="Impossible de charger vos projets pour le moment."
            onRetry={() => void drafts.refetch()}
          />
        ) : null}

        {drafts.data?.items.length === 0 ? (
          <EmptyState
            icon={<FileVideo className="size-10" strokeWidth={1.25} />}
            title="Aucun brouillon en cours"
            description="Créez votre première vidéo à partir d'un template : quelques clics suffisent pour la diffuser sur vos écrans."
            action={
              <Link to="/creer" className={buttonClasses({ size: 'sm' })}>
                Créer une vidéo
              </Link>
            }
          />
        ) : null}

        {drafts.data && drafts.data.items.length > 0 ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {drafts.data.items.map((project) => (
              <Card key={project.id} className="flex flex-col gap-3 p-5">
                <Badge tone="neutral" icon={<FileText className="size-3.5" />}>
                  Brouillon
                </Badge>

                <FileVideo aria-hidden className="size-8 text-ink-300" strokeWidth={1.25} />

                <h3 className="font-semibold">{project.name}</h3>
                <p className="text-sm text-ink-500">
                  Modifié {formatRelative(project.lastTouchedAt)}. Reprenez la création là où
                  vous l'avez laissée.
                </p>

                <Link
                  to="/videos"
                  className="mt-auto inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 hover:underline"
                >
                  Continuer
                  <ArrowRight aria-hidden className="size-4" />
                </Link>
              </Card>
            ))}
          </div>
        ) : null}
      </section>

      <section aria-labelledby="screens-heading">
        <SectionHeader title="Mes écrans" />
        <h2 id="screens-heading" className="sr-only">
          Mes écrans
        </h2>

        {screens.isPending ? <LoadingState /> : null}

        {screens.data?.items.length === 0 ? (
          <EmptyState
            title="Aucun écran déclaré"
            description="Ajoutez un écran pour commencer à diffuser. Un code d'appairage s'affichera sur votre TV ou votre boîtier Pixlide."
            action={
              <Link to="/ecrans" className={buttonClasses({ size: 'sm' })}>
                Ajouter un écran
              </Link>
            }
          />
        ) : null}

        {screens.data && screens.data.items.length > 0 ? (
          <Card className="divide-y divide-ink-100/70">
            {screens.data.items.map((screen) => (
              <div key={screen.id} className="flex flex-wrap items-center gap-4 px-5 py-4">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{screen.name}</p>
                  <p className="truncate text-sm text-ink-500">
                    {screen.establishmentName} · {screen.resolution}
                  </p>
                </div>

                <StatusDot online={screen.isOnline} />

                {screen.isAvailableForAdvertising ? <Badge tone="brand">Régie</Badge> : null}
              </div>
            ))}
          </Card>
        ) : null}
      </section>
    </div>
  );
}
